import React, { FC, useCallback, useMemo, useState } from 'react';

import { Icon } from '@iconify/react';
import { Box, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';

import Button from '~/components/atoms/buttons/Button';
import { getProductName } from '~/helpers/translation';
import useErrors from '~/hooks/useErrors';
import { useRestoreSubscription } from '~/modules/settings/hooks/useRestoreSubscription';
import {
  STAR_GOLD,
  TEAL,
} from '~/projects/polyllm-chat/components/SubscriptionModal/PolyllmRestoreModal/constants';
import analytics from '~/services/analytics';
import { useAppSelector } from '~/store/hooks/useAppSelector';
import { ProductType } from '~/types/product';
type Props = {
  onClose: () => void;
};
const PolyllmRestoreModal: FC<Props> = ({ onClose }) => {
  const { t } = useTranslation();
  const { t: tProduct } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { user_product } = useAppSelector((state) => state.profile);
  const { restoreSubscription } = useRestoreSubscription();
  const { reportUserErrors } = useErrors();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const restoredProduct: ProductType = useMemo(
    () => ({
      ...user_product,
      name: user_product.name,
      is_popular_ui: false,
      old_price_ui: 0,
      discount_percentage_ui: 0,
    }),
    [user_product],
  );
  const productDisplayName = getProductName(
    restoredProduct.id,
    restoredProduct.name,
    tProduct,
  );
  const productPriceLine = useMemo(() => {
    const { price, currency, regularity } = restoredProduct;
    const priceLabel = `${price / 100} ${currency}`;
    const periodLabel = t('general:per_period_v2', {
      period: t(`general:${regularity}`).toLowerCase(),
    });
    return `${priceLabel} ${periodLabel}`;
  }, [restoredProduct, t]);
  const handleRestoreClick = useCallback(async (): Promise<void> => {
    analytics.trackEvent('subscription - click not active sub screen', {
      sub: restoredProduct.name,
    });
    try {
      setIsSubmitting(true);
      await restoreSubscription();
      onClose();
    } catch (e: unknown) {
      reportUserErrors({
        error: e,
        method: 'restoreSubscription',
        userMessage: t('error:unable_restore_subscription'),
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [restoredProduct.name, restoreSubscription, onClose, reportUserErrors, t]);
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}
    >
      <Box
        sx={{
          width: '88px',
          height: '88px',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: '20px',
          background: `radial-gradient(circle at center, ${TEAL}33 0%, ${TEAL}00 70%)`,
          position: 'relative',
          '&::after': {
            content: '""',
            position: 'absolute',
            inset: 0,
            borderRadius: '50%',
            border: `1px solid ${isDark ? `${TEAL}66` : `${TEAL}33`}`,
          },
        }}
      >
        <Icon icon="ph:arrow-counter-clockwise-bold" width="32" color={TEAL} />
      </Box>

      <Typography
        sx={{
          fontWeight: 700,
          fontSize: '20px',
          lineHeight: '28px',
          textAlign: 'center',
          color: 'text.primary',
          mb: '8px',
        }}
      >
        {t('modal:polyllm_restore.title')}
      </Typography>

      <Typography
        sx={{
          fontSize: '14px',
          lineHeight: '20px',
          textAlign: 'center',
          color: 'text.secondary',
          mb: '24px',
          maxWidth: '320px',
        }}
      >
        {t('modal:polyllm_restore.subtitle')}
      </Typography>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          width: '100%',
          p: '12px 14px',
          mb: '24px',
          borderRadius: '12px',
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: isDark ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.02)',
        }}
      >
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: '50%',
            bgcolor: `${TEAL}1A`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mr: '12px',
            flexShrink: 0,
          }}
        >
          <Icon icon="ph:crown-simple-bold" width="18" color={STAR_GOLD} />
        </Box>
        <Box
          sx={{
            flexGrow: 1,
            minWidth: 0,
          }}
        >
          <Typography
            sx={{
              fontSize: '15px',
              fontWeight: 600,
              color: 'text.primary',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {productDisplayName}
          </Typography>
          <Typography
            sx={{
              fontSize: '12px',
              color: 'text.secondary',
            }}
          >
            {productPriceLine}
          </Typography>
        </Box>
        <Typography
          sx={{
            fontSize: '11px',
            fontWeight: 600,
            color: TEAL,
            border: `1px solid ${TEAL}`,
            borderRadius: '16px',
            px: '10px',
            py: '2px',
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
            flexShrink: 0,
            ml: '8px',
          }}
        >
          {t('modal:polyllm_restore.status_active')}
        </Typography>
      </Box>

      <Button
        variant="contained"
        color="primary"
        fullWidth
        onClick={handleRestoreClick}
        disabled={isSubmitting}
        sx={{
          borderRadius: '100px',
          height: 44,
          fontWeight: 700,
        }}
      >
        {t('modal:polyllm_restore.cta')}
      </Button>

      <Typography
        sx={{
          fontSize: '12px',
          color: 'text.secondary',
          textAlign: 'center',
          mt: '16px',
        }}
      >
        {t('modal:polyllm_restore.footer_hint')}
      </Typography>
    </Box>
  );
};
export default PolyllmRestoreModal;
